import { Box, Center, Flex, IconButton, InputLeftElement, useDisclosure } from '@chakra-ui/react';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';
import ChartMapImage from 'assets/images/chart_map.png';
import { UPGRADE_REDIRECT_LINK } from 'constants/links';
import { CLICK_ON_PAID_FEATURE, MAPS_SEARCH, MAP_CREATED, UPGRADE } from 'constants/mixpanel';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { getUserPlanProperty } from 'libs/third-party/Mixpanel/mixpanel.helper';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { SIDE_MENU_WIDGET_TYPES } from 'modules/Editor/components/SideMenuSettings/SideMenuSettings.config';
import { addNewWidget, getInitialWidgetPosition, useAppDispatch } from 'modules/Editor/store';
import { setWidgetSettingsView } from 'modules/Editor/store/editorSettingsSlice';
import { UserTypes, useUser } from 'modules/User';
import {
  AutoCompleteInput,
  AutoCompleteInputItem,
  AutoCompleteInputList,
} from 'modules/common/components/AutoCompleteInput';
import { AutoCompleteClearButton } from 'modules/common/components/AutoCompleteInput/AutoCompleteClearButton';
import { AutoCompleteInputGroup } from 'modules/common/components/AutoCompleteInput/AutoCompleteInput';
import { useToast } from 'modules/common/components/Toast';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { generateDefaultData as generateMapData } from 'widgets/MapWidget/MapWidget.helpers';
import { NewWidget } from 'widgets/Widget.types';

import { AllMapList } from './AllMapList';
import { MapHeader } from './MapHeader';
import { SelectionGrid } from './MapSelectionGrid';
import {
  ALL_MAP_TITLES,
  DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION,
  MAP_IMPORT_ERROR_TOAST,
} from './MapWidgetMenu.config';
import { fetchMap, getFilteredRegionSuggestions } from './MapWidgetMenu.helpers';
import { MapCreationType, MapMenuButtonConfig, MapWidgetSearchInputProps } from './MapWidgetMenu.types';

export const MapWidgetMenu = (): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.mapsMenu' });
  const { userType } = useUser();
  const toast = useToast();

  const { trackChameleon } = useChameleon();
  const dispatch = useAppDispatch();
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();

  // The alphabets being typed into the search input
  const [searchKeyword, setSearchKeyword] = useState('');

  // The chosen term that comes from clicking on the search suggestions, e.g. Arkansas
  const [chosenTerm, setChosenTerm] = useState('');

  const createWidgetData = useCallback(
    async (config: MapMenuButtonConfig) => {
      const { top, left } = getInitialWidgetPosition();

      // Here we fetch the map json file by its name when the button is clicked on.
      const onError = () => toast(MAP_IMPORT_ERROR_TOAST);
      const widgetData = generateMapData(await fetchMap(config, onError), { top, left }, config.label);
      return widgetData as NewWidget;
    },
    [toast],
  );

  const handleAddWidget = useCallback(
    async (config: MapMenuButtonConfig, buttonType: MapCreationType) => {
      const widgetData = await createWidgetData(config);

      if (widgetData) {
        dispatch(addNewWidget(widgetData));

        Mixpanel.track(MAP_CREATED.eventName, {
          from: buttonType,
          region: config.label,
        });

        if (SIDE_MENU_WIDGET_TYPES.includes(widgetData.widgetType)) {
          dispatch(setWidgetSettingsView(true));
        }
      }
    },
    [createWidgetData, dispatch],
  );

  const onGated = useCallback(() => {
    Mixpanel.track(UPGRADE.eventName, {
      Feature: UPGRADE.feature.PremiumMap,
      'User Plan': getUserPlanProperty(userType),
    });
    trackChameleon(UPGRADE.eventName);

    setAnalyticsUpgradeFeatureProp(UPGRADE.feature.PremiumMap, 'map widget');
    window.open(`${UPGRADE_REDIRECT_LINK}/${DEFAULT_MAP_WIDGET_MENU_BUTTON_PERMISSION}`, '_self');
  }, [userType, trackChameleon]);

  const onClickUpgrade = useCallback(onGated, [onGated]);

  const onClickUpgradeModal = useCallback(() => {
    onOpenUpgradeModal();
    Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
      Feature: UPGRADE.feature.PremiumMap,
      'User Plan': getUserPlanProperty(userType),
    });
  }, [onOpenUpgradeModal, userType]);

  return (
    <Flex padding={4} direction={'column'} gap={4} data-testid={'widget-menu-map-panel'} h={'full'} overflow={'auto'}>
      <CommonGatedUpgradeModal
        isOpen={isUpgradeModal}
        onClose={onCloseUpgradeModal}
        onClickUpgrade={onClickUpgrade}
        upgradePlan={UserTypes.Premium}
        modalImg={ChartMapImage}
        modalType={ModalUpgradeTypeEnum.MapCharts}
      />
      <MapWidgetSearchInput
        searchKeyword={searchKeyword}
        chosenTerm={chosenTerm}
        setSearchKeyword={setSearchKeyword}
        setChosenTerm={setChosenTerm}
      />
      {chosenTerm.length > 0 && (
        <SelectionGrid
          onClick={handleAddWidget}
          onClickUpgrade={onClickUpgradeModal}
          chosenTerm={chosenTerm}
          mapCreationType={MapCreationType.Search}
        />
      )}
      <MapHeader label={t('popularMapsLabel')} />
      <SelectionGrid
        onClick={handleAddWidget}
        onClickUpgrade={onClickUpgradeModal}
        mapCreationType={MapCreationType.Popular}
      />
      <AllMapList onClickUpgrade={onClickUpgradeModal} handleAddWidget={handleAddWidget} userType={userType} />
    </Flex>
  );
};

const MapWidgetSearchInput = ({
  searchKeyword,
  chosenTerm,
  setSearchKeyword,
  setChosenTerm,
}: MapWidgetSearchInputProps): ReactElement => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.mapsMenu' });
  const filteredRegionSuggestions = useMemo(
    () => getFilteredRegionSuggestions(ALL_MAP_TITLES, searchKeyword),
    [searchKeyword],
  );

  if (!searchKeyword.trim() && chosenTerm.length) {
    setChosenTerm('');
  }

  const onClearSearch = useCallback(() => {
    setSearchKeyword('');
    setChosenTerm('');
  }, [setChosenTerm, setSearchKeyword]);

  const handleInputChange = useCallback(
    (inputs: string[]) => {
      if (inputs.length === 0) return;
      setChosenTerm(inputs[0]);
      Mixpanel.track(MAPS_SEARCH.eventName, {
        query: inputs[0],
      });
    },
    [setChosenTerm],
  );

  const onIndividualInputChange = (text: string) => setSearchKeyword(text);
  const label = t('searchBarLabel');

  return (
    <Flex flexDirection={'column'} gap={filteredRegionSuggestions.length === 0 ? 0 : 4} alignSelf={'center'} w={304}>
      <AutoCompleteInput
        placeholder={label}
        onChange={handleInputChange}
        onIndividualInputChange={onIndividualInputChange}
        openListOnFocus
        submitValueOnEnter
        defaultValue={searchKeyword}
        focusOnMenuOpen
      >
        <AutoCompleteInputGroup>
          <InputLeftElement h={'full'} w={'fit-content'}>
            <IconButton
              size='xs'
              borderRadius='full'
              variant='unstyled'
              aria-label={label}
              tabIndex={-1} // Adding this to prevent focus on magnifier icon since enter is used to submit
              icon={
                <Center>
                  <SearchIcon stroke='var(--vg-colors-upgrade-blue-500' />
                </Center>
              }
              data-testid='search-icons-button'
              w={10}
            />
          </InputLeftElement>
          {searchKeyword && <AutoCompleteClearButton onClearInputEnd={onClearSearch} />}
        </AutoCompleteInputGroup>

        <AutoCompleteInputList showNoResultsFound useRelativePosition>
          {filteredRegionSuggestions.map((region, index) => (
            <AutoCompleteInputItem key={region} value={region} name={region}>
              <Box
                key={index}
                paddingY={1}
                paddingX={2}
                borderRadius={4}
                width={'100%'}
                height={'100%'}
                textAlign={'left'}
                fontSize={12}
                lineHeight={'16px'}
                textOverflow={'ellipsis'}
                overflow={'hidden'}
              >
                {region}
              </Box>
            </AutoCompleteInputItem>
          ))}
        </AutoCompleteInputList>
      </AutoCompleteInput>
    </Flex>
  );
};
